/**
 * Trace the goTo navigation graph of a form and report breaks.
 *
 * Navigation is not a single chain. A `decision` / `decisionWithOther` section
 * routes per option (`field.metadata.options[].goTo`), so its own `goTo: null`
 * is normal — the branches carry the paths. Every branch is expected to reach an
 * end of the form, and no section should sit off every path.
 *
 * Shared by the Studio "Check Paths" button and scripts/audit-form-paths.js, so
 * both read the same graph from either the editor state or a published template.
 */

const DECISION_TYPES = ["decision", "decisionWithOther"];

/** Sections that route per option rather than by their own goTo. */
export function isBranchingSection(type) {
  return DECISION_TYPES.includes(type);
}

const norm = (v) => (v == null ? "" : String(v).trim());

/**
 * Editor state → path-check nodes.
 * @param {Array} sections Studio sections (id, attributeName, type, goTo)
 * @param {Array} fields Studio fields (sectionId, metadata.options[])
 */
export function nodesFromStudioState(sections = [], fields = []) {
  return (sections || []).map((section) => {
    const options = [];
    if (isBranchingSection(section.type)) {
      (fields || [])
        .filter((f) => f.sectionId === section.id)
        .forEach((f) => {
          (f.metadata?.options || []).forEach((opt) => {
            options.push({
              value: norm(opt.value) || norm(opt.text),
              goTo: norm(opt.goTo),
            });
          });
        });
    }
    return {
      attributeName: norm(section.attributeName),
      title: section.title || "",
      type: section.type || "data",
      goTo: norm(section.goTo),
      options,
    };
  });
}

/**
 * Published template (bundle entry or publish payload) → path-check nodes.
 * @param {Object} template entry with formJSON.formData[0].sections
 */
export function nodesFromPublishedTemplate(template) {
  const sections = template?.formJSON?.formData?.[0]?.sections;
  return (Array.isArray(sections) ? sections : []).map((section) => {
    const options = [];
    if (isBranchingSection(section.type)) {
      (section.fields || []).forEach((f) => {
        (f.metadata?.options || []).forEach((opt) => {
          options.push({
            value: norm(opt.value) || norm(opt.text),
            goTo: norm(opt.goTo),
          });
        });
      });
    }
    return {
      attributeName: norm(section.attributeName),
      title: section.title || "",
      type: section.type || "data",
      goTo: norm(section.goTo),
      options,
    };
  });
}

/**
 * Gaps where the repair is unambiguous: a reachable, non-decision section whose
 * path ends early, sitting directly before an ORPHAN section in document order.
 * Linking the two is the only reading — the orphan has no other referrer, and
 * document order says it was meant to come next.
 *
 * Deliberately narrow. A stranded next-section is skipped (its referrer is the
 * real break), as is anything that would close a loop. Re-run after applying:
 * one link can make the following gap obvious in turn.
 *
 * @param {Array} nodes from nodesFromStudioState / nodesFromPublishedTemplate
 * @param {string} startAttributeName
 * @returns {Array<{ from: string, to: string, reason: string }>}
 */
export function findObviousGoToGaps(nodes = [], startAttributeName = "") {
  const result = checkSectionPaths(nodes, startAttributeName);
  const named = nodes.filter((n) => n.attributeName);
  const byName = new Map(named.map((n) => [n.attributeName, n]));
  const reachable = new Set(result.reachable);
  const orphans = new Set(result.orphans);

  // Would linking from → to close a loop? Only if `to` already leads back.
  const leadsBack = (to, from) => {
    const seen = new Set();
    const stack = [to];
    while (stack.length) {
      const cur = stack.pop();
      if (cur === from) return true;
      if (seen.has(cur)) continue;
      seen.add(cur);
      const node = byName.get(cur);
      if (!node) continue;
      edgesOf(node)
        .filter((e) => e.to && byName.has(e.to))
        .forEach((e) => stack.push(e.to));
    }
    return false;
  };

  const gaps = [];
  named.forEach((node, i) => {
    const next = named[i + 1];
    if (!next) return;
    if (isBranchingSection(node.type)) return;
    if (node.goTo) return;
    if (!reachable.has(node.attributeName)) return;
    if (!orphans.has(next.attributeName)) return;
    if (leadsBack(next.attributeName, node.attributeName)) return;
    gaps.push({
      from: node.attributeName,
      to: next.attributeName,
      reason: `path ends at "${node.attributeName}"; the next section "${next.attributeName}" is an orphan`,
    });
  });
  return gaps;
}

/** Outgoing edges for a node: [{ to, via }]; `to: ""` ends the form. */
function edgesOf(node) {
  if (!isBranchingSection(node.type)) {
    return [{ to: node.goTo, via: "goTo" }];
  }
  const edges = node.options.map((o) => ({ to: o.goTo, via: `option ${o.value || "(unnamed)"}` }));
  // A decision that also carries its own goTo still routes there when no option
  // matches, so it's a real edge — and worth flagging separately.
  if (node.goTo) edges.push({ to: node.goTo, via: "section goTo" });
  return edges.length ? edges : [];
}

/**
 * Check every path from the start section.
 *
 * @param {Array} nodes from nodesFromStudioState / nodesFromPublishedTemplate
 * @param {string} startAttributeName formJSON.startSectionAttributeName (optional)
 * @returns {{ ok: boolean, issues: Array, start: string, reachable: string[],
 *   unreachable: string[], orphans: string[], stranded: string[], ends: string[] }}
 *   `orphans` are unreachable with nothing pointing at them; `stranded` are
 *   pointed at only from sections that are themselves unreachable.
 *   Issue: { level: "error"|"warning", code, message, section? }
 */
export function checkSectionPaths(nodes = [], startAttributeName = "") {
  const issues = [];
  const add = (level, code, message, section) => issues.push({ level, code, message, section });

  const named = nodes.filter((n) => n.attributeName);
  if (named.length === 0) {
    add("error", "noSections", "Form has no sections with an attributeName");
    // Every key the normal return carries, so callers can read the result without
    // guarding this branch (sig-only / warning-only forms land here — they have no sections).
    return { ok: false, issues, start: "", reachable: [], unreachable: [], orphans: [], stranded: [], ends: [] };
  }

  const byName = new Map();
  named.forEach((n) => {
    if (byName.has(n.attributeName)) {
      add(
        "warning",
        "duplicateSection",
        `Duplicate section attributeName "${n.attributeName}" — navigation always lands on the first`,
        n.attributeName,
      );
      return;
    }
    byName.set(n.attributeName, n);
  });

  let start = norm(startAttributeName);
  if (start && !byName.has(start)) {
    add(
      "warning",
      "startSectionMissing",
      `startSectionAttributeName "${start}" is not a section in this form — tracing from the first section instead`,
    );
    start = "";
  }
  if (!start) start = named[0].attributeName;

  // Dangling targets and empty branch sets, reported per section.
  for (const node of byName.values()) {
    const edges = edgesOf(node);
    if (isBranchingSection(node.type) && edges.length === 0) {
      add(
        "error",
        "decisionNoBranches",
        `Decision section "${node.attributeName}" has no option goTo values — every path stops here`,
        node.attributeName,
      );
    }
    if (isBranchingSection(node.type) && node.goTo) {
      add(
        "warning",
        "decisionSectionGoTo",
        `Decision section "${node.attributeName}" also has a section-level goTo ("${node.goTo}") — options should carry the routing`,
        node.attributeName,
      );
    }
    edges.forEach((e) => {
      if (e.to && !byName.has(e.to)) {
        add(
          "error",
          "goToTargetMissing",
          `"${node.attributeName}" ${e.via} points at "${e.to}", which is not a section in this form`,
          node.attributeName,
        );
      }
    });
  }

  // Walk every path from the start, tracking the stack so a loop is caught with
  // the sections that form it. Visiting a node once is enough: paths beyond it
  // are identical whichever branch arrived.
  const reachable = new Set();
  const ends = new Set();
  const cyclesSeen = new Set();
  const stack = [];
  const onStack = new Set();

  const walk = (name) => {
    if (onStack.has(name)) {
      const loop = stack.slice(stack.indexOf(name)).concat(name);
      const key = loop.join(">");
      if (!cyclesSeen.has(key)) {
        cyclesSeen.add(key);
        add("error", "cycle", `Navigation loops: ${loop.join(" → ")} — this path never ends`, name);
      }
      return;
    }
    if (reachable.has(name)) return;
    reachable.add(name);

    const node = byName.get(name);
    const edges = edgesOf(node).filter((e) => !e.to || byName.has(e.to));
    if (edges.length === 0 || edges.every((e) => !e.to)) ends.add(name);

    stack.push(name);
    onStack.add(name);
    edges.filter((e) => e.to).forEach((e) => walk(e.to));
    onStack.delete(name);
    stack.pop();
  };

  walk(start);

  // Unreachable splits two ways, and the fix differs: an orphan needs someone to
  // point at it, a stranded section needs its referrer put back on a path.
  const unreachable = [...byName.keys()].filter((n) => !reachable.has(n));
  const referrersOf = new Map();
  for (const node of byName.values()) {
    edgesOf(node)
      .filter((e) => e.to && byName.has(e.to))
      .forEach((e) => {
        if (!referrersOf.has(e.to)) referrersOf.set(e.to, new Set());
        referrersOf.get(e.to).add(node.attributeName);
      });
  }
  const orphans = [];
  const stranded = [];
  unreachable.forEach((n) => {
    const referrers = [...(referrersOf.get(n) || [])].filter((r) => r !== n);
    if (referrers.length === 0) {
      orphans.push(n);
      add("error", "orphanSection", `"${n}" is unreachable — no goTo in this form points at it`, n);
    } else {
      stranded.push(n);
      add(
        "error",
        "strandedSection",
        `"${n}" is unreachable — only ${referrers.map((r) => `"${r}"`).join(", ")} point${referrers.length === 1 ? "s" : ""} at it, and ${referrers.length === 1 ? "it is" : "those are"} unreachable too`,
        n,
      );
    }
  });

  // A path that stops before the last section is legal but usually unintended.
  const lastName = named[named.length - 1].attributeName;
  [...ends]
    .filter((n) => n !== lastName)
    .forEach((n) =>
      add(
        "warning",
        "endsEarly",
        `A path ends at "${n}", which is not the last section ("${lastName}")`,
        n,
      ),
    );

  return {
    ok: issues.every((i) => i.level !== "error"),
    issues,
    start,
    reachable: [...reachable],
    unreachable,
    orphans,
    stranded,
    ends: [...ends],
  };
}
